import { api } from "@/shared/api";

/**
 * KB 内容审批台(Batch D6 maker-checker 的 checker 侧)。
 *
 * 与产品候选审批台(`kbProposalService`)是**同一个审批台的两个 tab**:那边审"要不要把这个产品加进
 * 产品树",这边审"要不要让这份内容能被客服/官网检索到"。门控口径完全一致 —— 后端锁
 * `@sec.isImpersonating()`(design §7.1「审批=保持平台超管代客态」),租户角色含 TENANT_ADMIN 一律 403。
 *
 * 提名侧不在这个 service 里:租户自己传文档(`knowledgeService.uploadDocument`)就是提名,后端按
 * **运行时身份**判 —— 代客态=平台超管本人直传生效,否则落 pending 等审。
 *
 * `reviewedBy` **不由前端发**:后端从登录态取真实身份,入参忽略,否则四眼审计可被伪造。
 *
 * 契约未进 api-spec.yaml(local-only schema gap),走 proposal
 * `docs/contracts/v23/proposals/2026-07-15-kb-product-proposal-and-node-media.md`。
 */

/** 治理轴。与 ingest 轴(pending|processing|done|failed)是**两个轴**,别混。 */
export type KbDocReviewStatus = "pending" | "approved" | "rejected";

/** 入流渠道(maker 侧);审批台据此换判断标准。 */
export type KbDocSourceChannel = "platform_direct" | "tenant_proposal" | "advisor_generated";

// local-only: backend schema gap — GET /platform/knowledge/documents
export interface KbDocReviewItem {
  id: number;
  title: string;
  /** ingest 轴:向量化跑完没有。可以 done 而 reviewStatus 仍是 pending。 */
  status: string;
  reviewStatus: KbDocReviewStatus;
  /**
   * 原件可整份下发(G1b,§5「answerable ≠ shareable_original」)。
   *
   * 与 `reviewStatus` 是**第三个轴**:那个管"这内容该不该被检索到(能不能拿来答)",
   * 这个管"这份**原件文件**能不能整份发给客户"。一份报价单可以拿来答价格,但整份 Excel
   * 里有内部成本列、隐藏 sheet、修订痕迹 —— 答错了能改口,文件发出去收不回来。
   *
   * 审批台**必须显示它**:一个看不见的放行位等于没有治理,运营没法复核自己放出去过什么。
   */
  shareableOriginal: boolean;
  /**
   * 第三根轴:对客可见性(2026-08-08 补进 DTO)。
   *
   * 客服公开检索要 `visibility === "public" && reviewStatus === "approved"` **两根同时满足**。
   * 审批台上不显示它,「已生效」栏就分不出哪些真的对客、哪些只是内部 —— 而 `approve` **只翻
   * reviewStatus 那一根**,所以「已生效」里躺着一堆客服其实检索不到的内容。
   */
  visibility: "public" | "internal" | "restricted";
  sourceChannel: KbDocSourceChannel;
  /** 提名人(maker)。四眼要能当场看出 maker ≠ checker。 */
  proposedBy: number | null;
  proposedByName: string | null;
  reviewedBy: number | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  nodeCode: string | null;
  chunkCount: number | null;
  createdAt: string;
}

export interface KbDocReviewBody {
  note?: string;
}

/**
 * V31:审批口按当前身份分流,两套端点是**同一个 service 的两个入口**。
 *
 * 此前只有 platform 那一套(后端还锁 `@sec.isImpersonating()`),于是**租户自己传的文档永远出不来** ——
 * 上传落 pending(TENANT_PROPOSAL),而租户侧没有任何入口能批它,客服闸门要 public ∧ approved。
 * 测试环境实测:TENANT_ADMIN 非代客态传一份,status=done 但 reviewStatus=pending,零入口。
 *
 * 代客态仍走平台口(平台超管在替租户操作,审计记在平台侧);租户管理员自己审走 tenant 口,
 * 后端用「提名人 ≠ 审批人」的四眼校验兜住「谁传的谁批」。
 */
const PLATFORM_BASE = "/platform/knowledge/documents";
const TENANT_BASE = "/tenant/knowledge/documents";

function isImpersonating(): boolean {
  try {
    return !!localStorage.getItem("xbgeo-impersonation");
  } catch {
    return false;
  }
}

/** 代客态 → 平台口;否则 → 租户口。 */
function base(): string {
  return isImpersonating() ? PLATFORM_BASE : TENANT_BASE;
}

/** 队列路径:平台口是 BASE 本身,租户口另开 /review-queue(BASE 上已有别的语义)。 */
function queuePath(status: KbDocReviewStatus): string {
  return isImpersonating()
    ? `${PLATFORM_BASE}?status=${status}`
    : `${TENANT_BASE}/review-queue?status=${status}`;
}

/** 平台审批总览行(2026-08-09):某租户有几件待审知识。tenantName 由前端拿租户列表自行 join。 */
export interface KbPendingReviewCount {
  tenantCode: string;
  pendingCount: number;
  /**
   * 待审**产品提名**数(1005 api 新增,可选)。api 同批把「只有提名、没有待审文档」的租户也列进来
   * (此时 pendingCount=0)。老后端不给这个字段 → undefined,展示与之前逐字一致。
   */
  pendingProposals?: number;
}

/** 1005:当前登录人的审批口径。`soloApprover=true` = 租户里除了我没有其他启用的管理员,自己提交的可以自己审。 */
export interface KbReviewPolicy {
  soloApprover: boolean;
}

export const kbDocReviewService = {
  /**
   * 1005 老板拍板「唯一管理员可自审」:后端判定为准,这里只拿来放开按钮 + 改提示。
   * 代客态审批人是平台超管、与租户提名人天然不同,不需要这个口 → 直接回 false。
   */
  reviewPolicy: async (): Promise<KbReviewPolicy> =>
    isImpersonating()
      ? { soloApprover: false }
      : api.get<KbReviewPolicy>(`${TENANT_BASE}/review-policy`),

  list: (status: KbDocReviewStatus = "pending") => api.get<KbDocReviewItem[]>(queuePath(status)),

  /**
   * 平台审批总览:各租户待审知识文档数(仅平台超管、**非代客态也可调** —— 它就是给
   * 「还没选租户」的入口页用的,此前那页是一句「请先代客」的死胡同)。
   */
  pendingSummary: () => api.get<KbPendingReviewCount[]>(`${PLATFORM_BASE}/pending-summary`),

  /** 批准 → 后端同步反写 ai 的 Qdrant review_status;反写失败会整单回滚(不会出现"批了但不生效")。 */
  approve: (id: number, body: KbDocReviewBody) =>
    api.post<KbDocReviewItem>(`${base()}/${id}/approve`, body),

  /** 驳回必带理由;后端**也会反写** ai —— 留在那边的 pending 点将来放宽 audience 就会漏出去。 */
  reject: (id: number, body: KbDocReviewBody) =>
    api.post<KbDocReviewItem>(`${base()}/${id}/reject`, body),

  /**
   * 放行/撤销**原件整份下发**(G1b,§5)。
   *
   * 与 `approve` 是**两个动作、两条审计**,不是它的副作用 —— D7 拍过「批准 ≠ 放开对客」:
   * 批准说的是"这内容可以拿来答",放行原件说的是"把这个文件整份发给客户"。合成一个的话,
   * 审批人点"批准"时根本不知道自己顺手把内部成本列发出去了。
   *
   * 后端口径:放行要求内容已 approved(双闸在人的流程上也是双闸);**撤销永远放行**
   * (安全阀,发现不该发的那一刻内容可能处在任何状态)。同样先反写 ai 再落库,失败整单回滚。
   */
  markShareable: (id: number, shareable: boolean, body: KbDocReviewBody) =>
    api.post<KbDocReviewItem>(`${base()}/${id}/shareable?shareable=${shareable}`, body),

  /**
   * 改**对客可见性**(2026-08-07 生产 P0 补)。
   *
   * 与 approve / markShareable 并列的第三个动作:approve 说「可以拿来答」,shareable 说
   * 「文件能整份发出去」,visibility 说「AI 客服到底看不看得见它」。事故当天 19 份文档
   * 全是 approved、管理端一片绿,而客服检索面是空的 —— 因为没有任何界面能表达这件事。
   *
   * 后端口径与 shareable 同款不对称:放开要求已 approved,**收回永远放行**。
   *
   * 端点同样按身份分流(见 base()):代客态走平台口,租户管理员自审走 tenant 口。
   */
  markVisibility: (id: number, visibility: "public" | "internal", body: KbDocReviewBody) =>
    api.post<KbDocReviewItem>(`${base()}/${id}/visibility?visibility=${visibility}`, body),
};
